import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminDashboardStats } from '../../types/admin';
import { fetchAdminStats } from '../../services/admin';
import { useAuth } from '../../hooks/useAuth';
import { hasAdminPermission, isAdminUser, isOwnerAdmin } from '../../utils/adminPermissions';

interface DashboardProps {
  billboardPreview?: React.ReactNode;
}

interface OverviewTileProps {
  eyebrow: string;
  title: string;
  value?: string;
  description: string;
}

const OverviewTile: React.FC<OverviewTileProps> = ({ eyebrow, title, value, description }) => (
  <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 text-sm text-slate-800 dark:text-slate-200 shadow-sm">
    <p className="text-xs text-slate-500 dark:text-slate-400">{eyebrow}</p>
    {value ? <p className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">{value}</p> : null}
    {!value ? <p className="mt-1 text-base font-semibold text-slate-900 dark:text-slate-100">{title}</p> : null}
    <p className="mt-2 text-[11px] text-slate-600 dark:text-slate-400">{description}</p>
  </div>
);

interface ActionTileProps {
  to: string;
  title: string;
  description: string;
}

const ActionTile: React.FC<ActionTileProps> = ({ to, title, description }) => (
  <Link
    to={to}
    className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 text-sm font-medium text-slate-800 dark:text-slate-200 shadow-sm transition hover:bg-white hover:shadow"
  >
    {title}
    <p className="mt-1 text-xs font-normal text-slate-500 dark:text-slate-400">{description}</p>
  </Link>
);

const Dashboard: React.FC<DashboardProps> = ({ billboardPreview }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminStats();
        if (!cancelled) {
          setStats(data);
        }
      } catch {
        if (!cancelled) {
          setError(
            'Admin overview data is not available right now. You can still use the tools below.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadStats();

    return () => {
      cancelled = true;
    };
  }, []);

  // The designated owner admin can see all admin tiles, including
  // user management and campaign packages.
  const isFullAdmin = isOwnerAdmin(user);
  const isAdmin = isAdminUser(user);
  const canSeeAdsManagement = isFullAdmin || hasAdminPermission(user, 'adAnalytics');
  const accessItems = [
    { label: 'User management', enabled: isAdmin },
    { label: 'Ads', enabled: canSeeAdsManagement },
    { label: 'Payments', enabled: hasAdminPermission(user, 'payments') },
    { label: 'Campaign packages', enabled: isFullAdmin },
    { label: 'Admin guide', enabled: hasAdminPermission(user, 'adminGuide') },
    { label: 'Billboard tools', enabled: hasAdminPermission(user, 'billboardTools') },
  ].filter((item) => item.enabled);

  const overviewStats: AdminDashboardStats = stats ?? {
    totalUsers: 0,
    totalAds: 0,
    totalPayments: 0,
    activeSessions: 0,
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Admin overview</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Use these tools to review ads, payments, users, and billboard health. Some tiles may be
          hidden based on your admin access level.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {isAdmin && (
          <OverviewTile
            eyebrow="Total users"
            title="Total users"
            value={String(overviewStats.totalUsers)}
            description="User and admin accounts."
          />
        )}
        {canSeeAdsManagement && (
          <OverviewTile
            eyebrow="Live creatives"
            title="Live creatives"
            value={String(overviewStats.totalAds)}
            description="Ads in the system."
          />
        )}
        <OverviewTile
          eyebrow="Payments recorded"
          title="Payments recorded"
          value={String(overviewStats.totalPayments)}
          description="Saved payment records."
        />
        {isAdmin && (
          <OverviewTile
            eyebrow="Active sessions"
            title="Active sessions"
            value={String(overviewStats.activeSessions)}
            description="Admins signed in now."
          />
        )}
      </div>

      {loading && !error && <p className="text-xs text-slate-500 dark:text-slate-400">Refreshing admin overview…</p>}
      {error && !loading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {isAdmin && (
          <ActionTile
            to="/admin/user-management"
            title="Manage users"
            description="View and manage accounts."
          />
        )}
        {canSeeAdsManagement && (
          <ActionTile
            to="/admin/ads"
            title="Ads management"
            description="Review and assign ads."
          />
        )}
        <ActionTile
          to="/payment"
          title="Payments"
          description="Review recent payments."
        />
        {isFullAdmin && (
          <ActionTile
            to="/admin/campaign-packages"
            title="Campaign packages"
            description="Edit packages and pricing."
          />
        )}
        {hasAdminPermission(user, 'adminGuide') && (
          <ActionTile
            to="/admin/guide"
            title="Admin guide"
            description="Step-by-step admin guide."
          />
        )}
        {isAdmin && (
          <ActionTile
            to="/admin/zoning"
            title="Zoning Management"
            description="Manage billboard zoning laws by jurisdiction."
          />
        )}
        {hasAdminPermission(user, 'adAnalytics') && (
          <ActionTile
            to="/admin/analytics"
            title="Ad Analytics"
            description="Campaign metrics and demographics."
          />
        )}
        <ActionTile
          to="/ai-video"
          title="AI Video Generator"
          description="Generate campaign videos with AI."
        />
      </div>

      <div className="space-y-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-4">
        <div className="grid gap-3 lg:grid-cols-[1.3fr_0.7fr]">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Access at a glance</h3>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              The tools available to this admin session right now.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {accessItems.map((item) => (
                <span
                  key={item.label}
                  className="inline-flex items-center rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white"
                >
                  {item.label}
                </span>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Current access
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {isFullAdmin ? 'Full admin' : isAdmin ? 'Creator admin' : 'Read only'}
            </p>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              {isFullAdmin
                ? 'You can manage users, packages, analytics, payments, and billboard tools.'
                : isAdmin
                  ? 'You can manage the tools granted to this admin account from the user-management screen.'
                  : 'Some admin areas are hidden until access is granted.'}
            </p>
          </div>
        </div>

        {billboardPreview && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Billboard test player</h3>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                  A widescreen preview of the live billboard experience.
                </p>
              </div>
              <div className="rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-400">
                Preview mode
              </div>
            </div>

            <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-950 p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
              <div className="rounded-lg border border-white/10 bg-black/80 p-2">
                {billboardPreview}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
export { Dashboard };
